/**
 * useVault: Custom hook for all vault operations (CRUD, upload, navigation, search/sort, sharing, colors)
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import JSZip from 'jszip';
import type {
  VaultFolder,
  VaultFile,
  VaultSortField,
  VaultSortDir,
  VaultBreadcrumb,
  UploadProgress,
  VaultVisibility,
  VaultFolderShare,
  VaultUser,
  CreateFolderOptions,
} from '../types/vault.types';
import { logError } from "../lib/logError";

interface UseVaultOptions {
  projectId: string;
  userId: string;
}

export function useVault({ projectId, userId }: UseVaultOptions) {
  // ─── State ────────────────────────────────────────────────────────────────
  const [folders, setFolders] = useState<VaultFolder[]>([]);
  const [files, setFiles] = useState<VaultFile[]>([]);
  const [allFolders, setAllFolders] = useState<VaultFolder[]>([]); // all project folders for tree
  const [allFiles, setAllFiles] = useState<VaultFile[]>([]); // all project files for search
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<VaultBreadcrumb[]>([{ id: null, name: 'Vault' }]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<VaultSortField>('created_at');
  const [sortDir, setSortDir] = useState<VaultSortDir>('desc');
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [vaultUsers, setVaultUsers] = useState<VaultUser[]>([]);

  const abortRef = useRef(false);

  // ─── Fetch current folder contents ────────────────────────────────────────
  const fetchContents = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch subfolders of current folder
      const folderQuery = supabase
        .from('vault_folders')
        .select('*')
        .eq('project_id', projectId);

      if (currentFolderId) {
        folderQuery.eq('parent_folder_id', currentFolderId);
      } else {
        folderQuery.is('parent_folder_id', null);
      }

      const { data: folderData, error: folderError } = await folderQuery.order('name');
      if (folderError) throw folderError;

      // Fetch files in current folder
      const fileQuery = supabase
        .from('vault_files')
        .select('*')
        .eq('project_id', projectId);

      if (currentFolderId) {
        fileQuery.eq('folder_id', currentFolderId);
      } else {
        fileQuery.is('folder_id', null);
      }

      const { data: fileData, error: fileError } = await fileQuery.order(sortBy, { ascending: sortDir === 'asc' });
      if (fileError) throw fileError;

      // Fetch ALL folders for tree sidebar (RLS automatically filters visibility)
      const { data: allFolderData } = await supabase
        .from('vault_folders')
        .select('*')
        .eq('project_id', projectId)
        .order('name');

      // Fetch total stats + all files (for search)
      const { data: allFileData } = await supabase
        .from('vault_files')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      setFolders((folderData || []) as VaultFolder[]);
      setFiles(fileData || []);
      setAllFolders((allFolderData || []) as VaultFolder[]);
      setAllFiles(allFileData || []);
      setTotalFiles(allFileData?.length || 0);
      setTotalSize(allFileData?.reduce((sum, f) => sum + (Number(f.size_bytes) || 0), 0) || 0);
    } catch (err) {
      logError('[Vault] Error fetching contents:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentFolderId, sortBy, sortDir]);

  useEffect(() => {
    fetchContents();
  }, [fetchContents]);

  // ─── Fetch vault-authorized users ─────────────────────────────────────────
  const fetchVaultUsers = useCallback(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .eq('can_view_vault', true)
      .neq('id', userId)
      .order('full_name');

    if (error) {
      logError('[Vault] Error fetching vault users:', error);
      return;
    }

    setVaultUsers(
      (data || []).map((u: { id: string; full_name: string | null; email: string | null }) => ({
        id: u.id,
        full_name: u.full_name,
        email: u.email,
      }))
    );
  }, [userId]);

  useEffect(() => {
    fetchVaultUsers();
  }, [fetchVaultUsers]);

  // ─── Build breadcrumbs ────────────────────────────────────────────────────
  const buildBreadcrumbs = useCallback(async (folderId: string | null) => {
    const crumbs: VaultBreadcrumb[] = [{ id: null, name: 'Vault' }];

    if (!folderId) {
      setBreadcrumbs(crumbs);
      return;
    }

    // Walk up the folder tree
    let currentId: string | null = folderId;
    const path: VaultBreadcrumb[] = [];

    while (currentId) {
      const { data } = await supabase
        .from('vault_folders')
        .select('id, name, parent_folder_id')
        .eq('id', currentId)
        .single() as { data: { id: string; name: string; parent_folder_id: string | null } | null };

      if (!data) break;
      path.unshift({ id: data.id, name: data.name });
      currentId = data.parent_folder_id;
    }

    setBreadcrumbs([...crumbs, ...path]);
  }, []);

  // ─── Navigation ───────────────────────────────────────────────────────────
  const navigateToFolder = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId);
    buildBreadcrumbs(folderId);
  }, [buildBreadcrumbs]);

  // ─── Folder Operations ────────────────────────────────────────────────────
  const createFolder = useCallback(async (options: CreateFolderOptions) => {
    const { name, parentId, visibility = 'public', color = '#3b82f6', sharedWith = [] } = options;

    const { data: newFolder, error } = await supabase.from('vault_folders').insert({
      project_id: projectId,
      parent_folder_id: parentId ?? currentFolderId ?? null,
      name: name.trim(),
      created_by: userId,
      visibility,
      color,
    }).select('id').single();

    if (error) throw error;

    // If shared, insert share records
    if (visibility === 'shared' && sharedWith.length > 0 && newFolder) {
      const shares = sharedWith.map(uid => ({
        folder_id: newFolder.id,
        shared_with_user_id: uid,
        shared_by: userId,
      }));

      const { error: shareError } = await supabase
        .from('vault_folder_shares')
        .insert(shares);

      if (shareError) {
        logError('[Vault] Error creating shares:', shareError);
      }
    }

    await fetchContents();
  }, [projectId, userId, currentFolderId, fetchContents]);

  const renameFolder = useCallback(async (folderId: string, newName: string) => {
    const { error } = await supabase
      .from('vault_folders')
      .update({ name: newName.trim() })
      .eq('id', folderId);

    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  // Helper: recursively get all files in a folder tree
  const getFilesInFolderRecursive = useCallback(async function recurseFiles(folderId: string): Promise<VaultFile[]> {
    let result: VaultFile[] = [];

    // Files in this folder
    const { data: filesInFolder } = await supabase
      .from('vault_files')
      .select('*')
      .eq('folder_id', folderId);
    if (filesInFolder) result = [...result, ...filesInFolder];

    // Subfolders
    const { data: subfolders } = await supabase
      .from('vault_folders')
      .select('id')
      .eq('parent_folder_id', folderId);

    if (subfolders) {
      for (const sub of subfolders) {
        const subFiles = await recurseFiles(sub.id);
        result = [...result, ...subFiles];
      }
    }

    return result;
  }, []);

  // Gather subfolder IDs recursively
  const getSubfoldersRecursive = useCallback(async function recurseFolders(folderId: string): Promise<string[]> {
    const ids: string[] = [];
    const { data: subs } = await supabase
      .from('vault_folders')
      .select('id, name')
      .eq('parent_folder_id', folderId);
    if (subs) {
      for (const s of subs) {
        ids.push(s.id);
        const deeper = await recurseFolders(s.id);
        ids.push(...deeper);
      }
    }
    return ids;
  }, []);

  const deleteFolder = useCallback(async (folderId: string) => {
    // Step 1: Get all files recursively (for storage cleanup + DB deletion)
    const filesToDelete = await getFilesInFolderRecursive(folderId);

    // Step 2: Delete from storage bucket
    if (filesToDelete.length > 0) {
      const paths = filesToDelete.map(f => f.storage_path);
      await supabase.storage.from('vault-files').remove(paths);
    }

    // Step 3: Explicitly delete file DB records BEFORE folder deletion
    // (FK is SET NULL not CASCADE, so files would be orphaned otherwise)
    if (filesToDelete.length > 0) {
      const fileIds = filesToDelete.map(f => f.id);
      // Supabase .in() has a limit, batch in groups of 100
      for (let i = 0; i < fileIds.length; i += 100) {
        const batch = fileIds.slice(i, i + 100);
        await supabase.from('vault_files').delete().in('id', batch);
      }
    }

    // Step 4: Delete folder (cascade deletes subfolders + shares)
    const { error } = await supabase
      .from('vault_folders')
      .delete()
      .eq('id', folderId);

    if (error) throw error;
    await fetchContents();
  }, [fetchContents, getFilesInFolderRecursive]);

  // ─── Folder Visibility & Color ────────────────────────────────────────────
  const updateFolderVisibility = useCallback(async (
    folderId: string,
    visibility: VaultVisibility,
    sharedWith?: string[],
  ) => {
    const { error } = await supabase
      .from('vault_folders')
      .update({ visibility })
      .eq('id', folderId);

    if (error) throw error;

    // If changing to shared, update share records
    if (visibility === 'shared' && sharedWith) {
      // Remove existing shares
      await supabase
        .from('vault_folder_shares')
        .delete()
        .eq('folder_id', folderId);

      // Insert new shares
      if (sharedWith.length > 0) {
        const shares = sharedWith.map(uid => ({
          folder_id: folderId,
          shared_with_user_id: uid,
          shared_by: userId,
        }));
        await supabase.from('vault_folder_shares').insert(shares);
      }
    } else if (visibility !== 'shared') {
      // Clear shares if no longer shared
      await supabase
        .from('vault_folder_shares')
        .delete()
        .eq('folder_id', folderId);
    }

    await fetchContents();
  }, [userId, fetchContents]);

  const updateFolderColor = useCallback(async (folderId: string, color: string) => {
    const { error } = await supabase
      .from('vault_folders')
      .update({ color })
      .eq('id', folderId);

    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  // ─── Folder Sharing ───────────────────────────────────────────────────────
  const getFolderShares = useCallback(async (folderId: string): Promise<VaultFolderShare[]> => {
    const { data, error } = await supabase
      .from('vault_folder_shares')
      .select('*, profiles!vault_folder_shares_shared_with_user_id_fkey(full_name, email)')
      .eq('folder_id', folderId);

    if (error) {
      logError('[Vault] Error fetching shares:', error);
      return [];
    }

    return (data || []).map((share: Record<string, unknown>) => {
      const profiles = share.profiles as { full_name: string | null; email: string | null } | null;
      return {
        id: share.id as string,
        folder_id: share.folder_id as string,
        shared_with_user_id: share.shared_with_user_id as string,
        shared_by: share.shared_by as string | null,
        created_at: share.created_at as string,
        shared_with_name: profiles?.full_name ?? undefined,
        shared_with_email: profiles?.email ?? undefined,
      };
    });
  }, []);

  const shareFolderWith = useCallback(async (folderId: string, userIds: string[]) => {
    if (userIds.length === 0) return;

    const shares = userIds.map(uid => ({
      folder_id: folderId,
      shared_with_user_id: uid,
      shared_by: userId,
    }));

    const { error } = await supabase
      .from('vault_folder_shares')
      .upsert(shares, { onConflict: 'folder_id,shared_with_user_id' });

    if (error) throw error;
  }, [userId]);

  const unshareFolderWith = useCallback(async (folderId: string, targetUserId: string) => {
    const { error } = await supabase
      .from('vault_folder_shares')
      .delete()
      .eq('folder_id', folderId)
      .eq('shared_with_user_id', targetUserId);

    if (error) throw error;
  }, []);


  /**
   * Preview what will be deleted when a folder is removed.
   * Used by the confirmation popup to show detailed breakdown.
   */
  const previewFolderDelete = useCallback(async (folderId: string) => {
    const files = await getFilesInFolderRecursive(folderId);
    const subfolderIds = await getSubfoldersRecursive(folderId);
    return {
      fileCount: files.length,
      subfolderCount: subfolderIds.length,
      totalSizeBytes: files.reduce((sum, f) => sum + (f.size_bytes || 0), 0),
    };
  }, [getFilesInFolderRecursive, getSubfoldersRecursive]);

  // ─── File Upload ──────────────────────────────────────────────────────────
  const uploadFiles = useCallback(async (
    fileList: File[],
    targetFolderId?: string | null,
  ) => {
    if (fileList.length === 0) return;
    abortRef.current = false;
    setUploading(true);

    const folderId = targetFolderId ?? currentFolderId ?? null;

    // Build folder path for storage
    let folderPath = '';
    if (folderId) {
      const crumbs: string[] = [];
      let walkId: string | null = folderId;
      while (walkId) {
        const { data } = await supabase
          .from('vault_folders')
          .select('id, name, parent_folder_id')
          .eq('id', walkId)
          .single() as { data: { id: string; name: string; parent_folder_id: string | null } | null };
        if (!data) break;
        crumbs.unshift(data.name.replace(/[^a-zA-Z0-9-_ ]/g, '_'));
        walkId = data.parent_folder_id;
      }
      folderPath = crumbs.join('/') + '/';
    }

    // Init progress state
    const progressArr: UploadProgress[] = fileList.map((f, i) => ({
      fileIndex: i,
      fileName: f.name,
      fileSize: f.size,
      progress: 0,
      status: 'pending',
    }));
    setUploadProgress([...progressArr]);

    for (let i = 0; i < fileList.length; i++) {
      if (abortRef.current) break;

      const file = fileList[i];
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${projectId}/${folderPath}${timestamp}_${safeName}`;

      // Update status to uploading
      progressArr[i] = { ...progressArr[i], status: 'uploading', progress: 10 };
      setUploadProgress([...progressArr]);

      try {
        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('vault-files')
          .upload(storagePath, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) throw uploadError;

        progressArr[i] = { ...progressArr[i], progress: 70 };
        setUploadProgress([...progressArr]);

        // Insert metadata
        const { error: dbError } = await supabase.from('vault_files').insert({
          project_id: projectId,
          folder_id: folderId,
          name: file.name,
          storage_path: storagePath,
          size_bytes: file.size,
          mime_type: file.type || null,
          uploaded_by: userId,
        });

        if (dbError) throw dbError;

        progressArr[i] = { ...progressArr[i], progress: 100, status: 'complete' };
        setUploadProgress([...progressArr]);
      } catch (err) {
        logError(`[Vault] Upload failed for ${file.name}:`, err);
        progressArr[i] = {
          ...progressArr[i],
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        };
        setUploadProgress([...progressArr]);
      }
    }

    setUploading(false);
    await fetchContents();

    // Clear progress after delay
    setTimeout(() => {
      setUploadProgress([]);
    }, 3000);
  }, [projectId, userId, currentFolderId, fetchContents]);

  const cancelUpload = useCallback(() => {
    abortRef.current = true;
  }, []);

  // ─── File Operations ──────────────────────────────────────────────────────
  const renameFile = useCallback(async (fileId: string, newName: string) => {
    const { error } = await supabase
      .from('vault_files')
      .update({ name: newName.trim() })
      .eq('id', fileId);
    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  const deleteFile = useCallback(async (fileId: string) => {
    // Get file to find storage path
    const { data: file } = await supabase
      .from('vault_files')
      .select('storage_path')
      .eq('id', fileId)
      .single();

    if (file) {
      await supabase.storage.from('vault-files').remove([file.storage_path]);
    }

    const { error } = await supabase
      .from('vault_files')
      .delete()
      .eq('id', fileId);
    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  const deleteFiles = useCallback(async (fileIds: string[]) => {
    // Get all file storage paths
    const { data: filesToDelete } = await supabase
      .from('vault_files')
      .select('id, storage_path')
      .in('id', fileIds);

    if (filesToDelete && filesToDelete.length > 0) {
      const paths = filesToDelete.map(f => f.storage_path);
      await supabase.storage.from('vault-files').remove(paths);
    }

    const { error } = await supabase
      .from('vault_files')
      .delete()
      .in('id', fileIds);
    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  const moveFile = useCallback(async (fileId: string, targetFolderId: string | null) => {
    const { error } = await supabase
      .from('vault_files')
      .update({ folder_id: targetFolderId })
      .eq('id', fileId);
    if (error) throw error;
    await fetchContents();
  }, [fetchContents]);

  // Move a folder into another folder (change parent_folder_id)
  const moveFolder = useCallback(async (folderId: string, targetParentId: string | null) => {
    // Prevent moving into itself
    if (folderId === targetParentId) return;

    // Prevent moving into own descendant (circular reference)
    const isDescendant = (parentId: string | null, checkId: string): boolean => {
      if (!parentId) return false;
      if (parentId === checkId) return true;
      const parent = allFolders.find(f => f.id === parentId);
      return parent ? isDescendant(parent.parent_folder_id, checkId) : false;
    };

    if (targetParentId && isDescendant(targetParentId, folderId)) {
      console.warn('[Vault] Cannot move folder into its own descendant');
      return;
    }

    const { error } = await supabase
      .from('vault_folders')
      .update({ parent_folder_id: targetParentId })
      .eq('id', folderId);
    if (error) throw error;
    await fetchContents();
  }, [fetchContents, allFolders]);

  const copyFile = useCallback(async (fileId: string, targetFolderId: string | null) => {
    // Get source file
    const { data: source } = await supabase
      .from('vault_files')
      .select('*')
      .eq('id', fileId)
      .single();

    if (!source) throw new Error('Source file not found');

    // Download from storage
    const { data: blob, error: dlError } = await supabase.storage
      .from('vault-files')
      .download(source.storage_path);

    if (dlError || !blob) throw dlError || new Error('Failed to download file');

    // Upload copy
    const timestamp = Date.now();
    const newPath = source.storage_path.replace(/\/([^/]+)$/, `/${timestamp}_copy_$1`);

    const { error: upError } = await supabase.storage
      .from('vault-files')
      .upload(newPath, blob, { cacheControl: '3600', upsert: false });

    if (upError) throw upError;

    // Insert copy metadata
    const { error: dbError } = await supabase.from('vault_files').insert({
      project_id: projectId,
      folder_id: targetFolderId,
      name: `Copy of ${source.name}`,
      storage_path: newPath,
      size_bytes: source.size_bytes,
      mime_type: source.mime_type,
      uploaded_by: userId,
    });

    if (dbError) throw dbError;
    await fetchContents();
  }, [projectId, userId, fetchContents]);

  const getDownloadUrl = useCallback(async (storagePath: string): Promise<string> => {
    const { data, error } = await supabase.storage
      .from('vault-files')
      .createSignedUrl(storagePath, 3600); // 1 hour

    if (error || !data?.signedUrl) throw error || new Error('Failed to create download URL');
    return data.signedUrl;
  }, []);

  // ─── Duplicate Detection ──────────────────────────────────────────────────
  /**
   * Check if any of the given file names already exist in the target folder.
   * Returns an array of existing VaultFile records that match.
   */
  const checkDuplicates = useCallback(async (
    fileNames: string[],
    targetFolderId: string | null,
  ): Promise<VaultFile[]> => {
    if (fileNames.length === 0) return [];

    const query = supabase
      .from('vault_files')
      .select('*')
      .eq('project_id', projectId)
      .in('name', fileNames);

    if (targetFolderId) {
      query.eq('folder_id', targetFolderId);
    } else {
      query.is('folder_id', null);
    }

    const { data, error } = await query;
    if (error) {
      logError('[Vault] Duplicate check failed:', error);
      return [];
    }
    return (data || []) as VaultFile[];
  }, [projectId]);

  /**
   * Replace an existing file: delete from storage + DB, then upload the new one.
   */
  const replaceFile = useCallback(async (
    existingFileId: string,
    newFile: File,
    targetFolderId: string | null,
  ) => {
    // Delete old file from storage + DB
    const { data: oldFile } = await supabase
      .from('vault_files')
      .select('storage_path')
      .eq('id', existingFileId)
      .single();

    if (oldFile) {
      await supabase.storage.from('vault-files').remove([oldFile.storage_path]);
    }
    await supabase.from('vault_files').delete().eq('id', existingFileId);

    // Upload new version
    const folderId = targetFolderId;
    let folderPath = '';
    if (folderId) {
      const crumbs: string[] = [];
      let walkId: string | null = folderId;
      while (walkId) {
        const { data } = await supabase
          .from('vault_folders')
          .select('id, name, parent_folder_id')
          .eq('id', walkId)
          .single() as { data: { id: string; name: string; parent_folder_id: string | null } | null };
        if (!data) break;
        crumbs.unshift(data.name.replace(/[^a-zA-Z0-9-_ ]/g, '_'));
        walkId = data.parent_folder_id;
      }
      folderPath = crumbs.join('/') + '/';
    }

    const timestamp = Date.now();
    const safeName = newFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `${projectId}/${folderPath}${timestamp}_${safeName}`;

    const { error: upError } = await supabase.storage
      .from('vault-files')
      .upload(storagePath, newFile, { cacheControl: '3600', upsert: false });
    if (upError) throw upError;

    const { error: dbError } = await supabase.from('vault_files').insert({
      project_id: projectId,
      folder_id: folderId,
      name: newFile.name,
      storage_path: storagePath,
      size_bytes: newFile.size,
      mime_type: newFile.type || null,
      uploaded_by: userId,
    });
    if (dbError) throw dbError;
  }, [projectId, userId]);

  // ─── Search & Sort ────────────────────────────────────────────────────────
  const toggleSortDir = useCallback(() => {
    setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
  }, []);

  const isSearchActive = searchQuery.trim().length > 0;

  // When searching, query ALL files/folders across the project; otherwise show current folder only
  const filteredFolders = useMemo(() => {
    if (!isSearchActive) return folders;
    const q = searchQuery.toLowerCase();
    return allFolders.filter(f => f.name.toLowerCase().includes(q));
  }, [isSearchActive, searchQuery, folders, allFolders]);

  const filteredFiles = useMemo(() => {
    if (!isSearchActive) return files;
    const q = searchQuery.toLowerCase();
    return allFiles.filter(f => f.name.toLowerCase().includes(q));
  }, [isSearchActive, searchQuery, files, allFiles]);

  // ─── Bulk Download (ZIP for multiple files) ───────────────────────────────
  const bulkDownload = useCallback(async (fileIds: string[]) => {
    const targets = allFiles.filter(f => fileIds.includes(f.id));
    if (targets.length === 0) return;

    // Single file, just download directly
    if (targets.length === 1) {
      const url = await getDownloadUrl(targets[0].storage_path);
      const a = document.createElement('a');
      a.href = url;
      a.download = targets[0].name;
      a.click();
      return;
    }

    // Multiple files, zip them
    const zip = new JSZip();

    for (const file of targets) {
      try {
        const url = await getDownloadUrl(file.storage_path);
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(file.name, blob);
      } catch (err) {
        logError(`[Vault] Failed to add ${file.name} to zip:`, err);
      }
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(zipBlob);
    a.download = `vault-files-${targets.length}.zip`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [allFiles, getDownloadUrl]);

  return {
    // State
    folders: filteredFolders,
    files: filteredFiles,
    allFolders,
    currentFolderId,
    breadcrumbs,
    loading,
    uploading,
    uploadProgress,

    // Folder ops
    createFolder,
    renameFolder,
    deleteFolder,
    previewFolderDelete,
    moveFolder,

    // Folder visibility & color
    updateFolderVisibility,
    updateFolderColor,

    // Sharing
    getFolderShares,
    shareFolderWith,
    unshareFolderWith,
    vaultUsers,

    // File ops
    uploadFiles,
    cancelUpload,
    renameFile,
    deleteFile,
    deleteFiles,
    moveFile,
    copyFile,
    getDownloadUrl,

    // Navigation
    navigateToFolder,

    // Search & Sort
    searchQuery,
    setSearchQuery,
    sortBy,
    sortDir,
    setSortBy,
    toggleSortDir,

    // Summary
    totalFiles,
    totalSize,

    // Search state
    isSearchActive,

    // Bulk download
    bulkDownload,

    // Duplicate detection
    checkDuplicates,
    replaceFile,

    // Refresh
    refresh: fetchContents,
  };
}
